import React, { useState, useEffect, useContext } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import './ChangeOrders.css';

const ChangeOrderModal = ({ show, onHide, project, onSuccess }) => {
  const { token } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextNumber, setNextNumber] = useState('CO-001');
  
  const [formData, setFormData] = useState({
    description: '',
    workItems: '• ',
    amount: '',
    paymentDueDate: ''
  });

  useEffect(() => {
    if (show && project?.ClientId) {
      fetchNextChangeOrderNumber();
    }
  }, [show, project]);

  const fetchNextChangeOrderNumber = async () => {
    try {
      const response = await fetch(`/api/change-orders/next-number/${project.ClientId}`, {
        headers: { 'x-auth-token': token }
      });
      if (response.ok) {
        const data = await response.json();
        setNextNumber(data.changeOrderNumber);
      }
    } catch (error) {
      console.error('Error fetching next change order number:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle work items textarea with auto bullet points
  const handleWorkItemsChange = (e) => {
    let value = e.target.value;
    
    // Get cursor position
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);
    
    // Check if user just pressed Enter (newline was just added)
    if (textBeforeCursor.endsWith('\n') && !textBeforeCursor.endsWith('\n• ')) {
      // Add bullet point after the newline
      value = textBeforeCursor + '• ' + textAfterCursor;
      
      // Update state and then set cursor position
      setFormData(prev => ({ ...prev, workItems: value }));
      
      // Need to set cursor position after React updates the textarea
      setTimeout(() => {
        const textarea = e.target;
        const newPosition = cursorPosition + 2; // +2 for "• "
        textarea.selectionStart = newPosition;
        textarea.selectionEnd = newPosition;
      }, 0);
      return;
    }
    
    setFormData(prev => ({ ...prev, workItems: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Please enter a description for the change order');
      return;
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse work items from textarea - split by newlines and clean up
      const workItems = formData.workItems
        .split('\n')
        .map(item => item.replace(/^•\s*/, '').trim()) // Remove bullet and trim
        .filter(item => item !== ''); // Remove empty lines

      const response = await fetch('/api/change-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          projectId: project.ProjectId,
          description: formData.description,
          workItems,
          amount: parseFloat(formData.amount),
          paymentDueDate: formData.paymentDueDate || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create change order');
      }

      const data = await response.json();
      
      // Reset form
      setFormData({
        description: '',
        workItems: '• ',
        amount: '',
        paymentDueDate: ''
      });
      
      // Close this modal first, then trigger success callback
      // The parent component will show the email preview modal
      onHide();
      onSuccess(data);
    } catch (error) {
      console.error('Error creating change order:', error);
      toast.error(error.message || 'Failed to create change order');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Change Order - {nextNumber}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Client & Project Info (Read-only) */}
          <div className="info-section mb-4">
            <h6 className="text-muted mb-3">Client Information</h6>
            <Row>
              <Col md={6}>
                <p className="mb-1"><strong>Client:</strong> {project?.ClientName}</p>
                <p className="mb-1"><strong>Address:</strong> {project?.Address}</p>
                <p className="mb-1">{project?.City}, {project?.State} {project?.ZipCode}</p>
              </Col>
              <Col md={6}>
                <p className="mb-1"><strong>Phone:</strong> {project?.Phone || 'N/A'}</p>
                <p className="mb-1"><strong>Email:</strong> {project?.Email || 'N/A'}</p>
              </Col>
            </Row>
            <hr />
            <p className="mb-0"><strong>Project:</strong> {project?.ProjectName}</p>
          </div>

          {/* Description */}
          <Form.Group className="mb-4">
            <Form.Label><strong>Description / Reason for Change Order *</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the reason for this change order (e.g., 'During demolition, we discovered that the subfloor beneath the bathroom tile is rotted and requires replacement.')"
              required
            />
            <Form.Text className="text-muted">
              Explain why this additional work is needed.
            </Form.Text>
          </Form.Group>

          {/* Work Items */}
          <Form.Group className="mb-4">
            <Form.Label><strong>Work to be Performed</strong></Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              List the specific work items. Press Enter for each new item (bullets added automatically).
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={5}
              value={formData.workItems}
              onChange={handleWorkItemsChange}
              placeholder="• First work item"
              style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
            />
          </Form.Group>

          {/* Amount and Due Date */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Change Order Amount *</strong></Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Payment Due Date</strong></Form.Label>
                <Form.Control
                  type="date"
                  name="paymentDueDate"
                  value={formData.paymentDueDate}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Leave blank for "Upon Approval"
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {formData.amount && (
            <Alert variant="info">
              <strong>Change Order Total:</strong> ${formatCurrency(formData.amount)}
              <br />
              <small>This amount will be added to the project's payment tracking.</small>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Change Order'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChangeOrderModal;
